// FILE PATH: eRFQFrontend/src/component/Logistic/LogisticSummaryTable.jsx

import React from "react";
import { Box, Typography } from "@mui/material";
import {
  buildExchangeRateMap,
  buildVendorSummaryGroups,
  formatAmount,
  getQuoteTotalsByCurrency,
  getQuoteTotalInInr,
  getSummaryCurrencies,
  shortCurrency,
} from "./logisticSummaryUtils";

const LogisticSummaryTable = ({ quotes, logisticDraft }) => {
  if (!quotes || quotes.length === 0) return null;

  const vendorGroups = buildVendorSummaryGroups(quotes);
  const allCurrencies = getSummaryCurrencies(quotes);
  const exchangeRateMap = buildExchangeRateMap(logisticDraft);
  const grossWeight = Number(logisticDraft?.grossWeight || 0);

  const renderCurrencyValue = (quote, currency) => {
    if (!quote) return "-";

    const totals = getQuoteTotalsByCurrency(quote);
    const amount = Number(totals[currency] || 0);

    return amount > 0 ? `${shortCurrency(currency)} ${formatAmount(amount)}` : "-";
  };

  const renderInrValue = (quote) => {
    if (!quote) return "-";

    const totalInInr = getQuoteTotalInInr(quote, exchangeRateMap);
    return totalInInr > 0 ? `INR ${formatAmount(totalInInr)}` : "-";
  };

  const renderCostPerKgValue = (quote) => {
    if (!quote || grossWeight <= 0) return "-";

    const totalInInr = getQuoteTotalInInr(quote, exchangeRateMap);
    if (totalInInr <= 0) return "-";

    return `INR ${formatAmount(totalInInr / grossWeight)}`;
  };

  const headerStyle = {
    border: "1px solid #2f2f2f",
    padding: "10px 12px",
    textAlign: "center",
    fontWeight: 700,
    backgroundColor: "#fff",
    whiteSpace: "nowrap",
  };

  const cellStyle = {
    border: "1px solid #2f2f2f",
    padding: "10px 12px",
    textAlign: "center",
    verticalAlign: "middle",
    backgroundColor: "#fff",
    minWidth: "150px",
  };

  const leftCellStyle = {
    ...cellStyle,
    textAlign: "left",
    fontWeight: 700,
    minWidth: "270px",
  };

  return (
    <Box className="dashboard-container" mb={3}>
      <Typography variant="h6" mb={2}>
        Quote wise Summary
      </Typography>

      <Box sx={{ overflowX: "auto" }}>
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            width: "max-content",
            minWidth: `${320 + vendorGroups.length * 300}px`,
          }}
        >
          <thead>
            <tr>
              <th style={{ ...headerStyle, minWidth: "270px", textAlign: "left" }}>
                Vendor
              </th>
              {vendorGroups.map((group) => (
                <th
                  key={group.vendorKey}
                  colSpan={group.finalQuote ? 2 : 1}
                  style={headerStyle}
                >
                  {group.vendorLabel}
                </th>
              ))}
            </tr>
            <tr>
              <th style={headerStyle}></th>
              {vendorGroups.map((group) => (
                <React.Fragment key={`${group.vendorKey}-subhead`}>
                  <th style={headerStyle}>Initial Quote</th>
                  {group.finalQuote && <th style={headerStyle}>Final Quote</th>}
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {allCurrencies.map((currency) => (
              <tr key={currency}>
                <td style={leftCellStyle}>{`Grand Total (${currency})`}</td>
                {vendorGroups.map((group) => (
                  <React.Fragment key={`${group.vendorKey}-${currency}`}>
                    <td style={cellStyle}>
                      {renderCurrencyValue(group.initialQuote, currency)}
                    </td>
                    {group.finalQuote && (
                      <td style={cellStyle}>
                        {renderCurrencyValue(group.finalQuote, currency)}
                      </td>
                    )}
                  </React.Fragment>
                ))}
              </tr>
            ))}

            <tr>
              <td style={leftCellStyle}>Total (INR - Indian Rupee)</td>
              {vendorGroups.map((group) => (
                <React.Fragment key={`${group.vendorKey}-inr`}>
                  <td style={cellStyle}>{renderInrValue(group.initialQuote)}</td>
                  {group.finalQuote && (
                    <td style={cellStyle}>{renderInrValue(group.finalQuote)}</td>
                  )}
                </React.Fragment>
              ))}
            </tr>

            <tr>
              <td style={leftCellStyle}>Cost per Kg</td>
              {vendorGroups.map((group) => (
                <React.Fragment key={`${group.vendorKey}-kg`}>
                  <td style={cellStyle}>{renderCostPerKgValue(group.initialQuote)}</td>
                  {group.finalQuote && (
                    <td style={cellStyle}>{renderCostPerKgValue(group.finalQuote)}</td>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export default LogisticSummaryTable;
